// ════════════════════════════════════════════════════════════════════
// LYRAEA — book.js   ·   AUDIOBOOKS FROM A DROPPED FILE
// ────────────────────────────────────────────────────────────────────
// An audiobook made in Readers (Lyraei Control Center) arrives as ONE MP3:
//   • CHAP/CTOC ID3 frames  — the chapters (Readers writes these itself)
//   • TXXX "LYRAEA_BOOK"    — the follow-along word timing, baked in by
//                             mass_transfer/tools/lyraea_bake_book.py
// A plain Readers export (no timing) still plays with chapters; any MP3
// with no chapters at all plays as a single chapter. A sidecar .json with
// the same payload can be dropped beside the MP3 instead of baking.
//
// ⚠ WHY LOCAL AND NOT THE CDN: an hour of speech is ~80 MB and the asset
//   host caps a file at 25 MB (see CDN.js). A dropped file plays from a
//   blob: URL through the SAME engine — lock screen, sleep timer, all of
//   it — and never touches Firebase or the catalogue. A blob URL dies on
//   reload, so the book is re-opened after a reload and RESUMES from the
//   position saved below (keyed by file name + size, not by the URL).
//
// ⚠ ID3 SIZE QUIRK: some writers store CHAP/CTOC sizes as plain integers
//   inside a v2.4 tag, where the spec says syncsafe. Each frame size is
//   tried both ways and the one that lands on the next frame wins —
//   same walk as the bake tool. Never "fix" this to syncsafe-only.
// ════════════════════════════════════════════════════════════════════

const ss = (b, i) => (b[i] << 21) | (b[i + 1] << 14) | (b[i + 2] << 7) | b[i + 3];
const be = (b, i) => ((b[i] << 24) >>> 0) + (b[i + 1] << 16) + (b[i + 2] << 8) + b[i + 3];
const isId = (b, i) => {
  for (let k = 0; k < 4; k++) {
    const c = b[i + k];
    if (!((c >= 48 && c <= 57) || (c >= 65 && c <= 90))) return false;
  }
  return true;
};

// Decode an ID3 text body (first byte = encoding).
function text(bytes) {
  if (!bytes.length) return "";
  const enc = bytes[0], body = bytes.subarray(1);
  const label = enc === 1 ? "utf-16" : enc === 2 ? "utf-16be" : enc === 3 ? "utf-8" : "latin1";
  return new TextDecoder(label).decode(body).replace(/\0+$/, "");
}

// Walk frames in [start,end) and call fn(id, bodyBytes).
function walk(b, start, end, ver, fn) {
  let i = start;
  while (i + 10 <= end && isId(b, i)) {
    const sizes = ver === 4 ? [ss(b, i + 4), be(b, i + 4)] : [be(b, i + 4)];
    let next = -1, size = 0;
    for (const n of sizes) {
      const j = i + 10 + n;
      if (j === end || (j < end && (b[j] === 0 || isId(b, j)))) { next = j; size = n; break; }
    }
    if (next < 0) break;   // unreadable frame — stop, keep what we have
    fn(String.fromCharCode(b[i], b[i + 1], b[i + 2], b[i + 3]), b.subarray(i + 10, i + 10 + size));
    i = next;
  }
}

// Read the tag at the head of the file. Only the tag is read into memory,
// never the audio — an 80 MB book costs a few hundred KB here.
export async function readTag(file) {
  const head = new Uint8Array(await file.slice(0, 10).arrayBuffer());
  if (head[0] !== 0x49 || head[1] !== 0x44 || head[2] !== 0x33) return {};
  const ver = head[3];
  const size = ss(head, 6);
  const b = new Uint8Array(await file.slice(10, 10 + size).arrayBuffer());
  const out = { chapters: [] };
  walk(b, 0, b.length, ver, (id, body) => {
    if (id === "TIT2") out.title = text(body);
    else if (id === "TPE1") out.artist = text(body);
    else if (id === "TXXX") {
      const t = text(body);
      const cut = t.indexOf("\0");
      if (cut > 0 && t.slice(0, cut) === "LYRAEA_BOOK") {
        try { out.book = JSON.parse(t.slice(cut + 1)); } catch { /* bad payload: chapters still work */ }
      }
    } else if (id === "CHAP") {
      const z = body.indexOf(0);
      const start = be(body, z + 1) / 1000, end = be(body, z + 5) / 1000;
      const ch = { start, end, title: "" };
      walk(body, z + 17, body.length, ver, (sid, sb) => { if (sid === "TIT2") ch.title = text(sb); });
      out.chapters.push(ch);
    }
  });
  out.chapters.sort((x, y) => x.start - y.start);
  return out;
}

// Build a playable track from dropped files (the audio, plus an optional
// sidecar .json). Returns a Track the engine can load, or throws a
// sentence a person can act on.
export async function openBook(files) {
  const list = Array.from(files || []);
  const audio = list.find((f) => /\.(mp3|m4a|m4b|ogg|opus|wav|flac|aac)$/i.test(f.name) || /^audio\//.test(f.type));
  if (!audio) throw new Error("Drop the audiobook's audio file (the MP3 from Readers).");
  const side = list.find((f) => /\.json$/i.test(f.name));

  const tag = /\.mp3$/i.test(audio.name) ? await readTag(audio).catch(() => ({})) : {};
  let book = tag.book || null;
  if (!book && side) {
    try { book = JSON.parse(await side.text()); } catch { throw new Error("That .json is not a Lyraea book file."); }
  }

  // Chapters: the baked payload is the richest source (it has words), the
  // CHAP frames next, else the whole file is one chapter.
  let chapters;
  if (book?.chapters?.length) {
    chapters = book.chapters.map((c) => ({ title: c.title, start: c.start, end: c.start + c.dur, w: c.w || [], t: c.t || [] }));
  } else if (tag.chapters?.length) {
    chapters = tag.chapters.map((c) => ({ ...c, w: [], t: [] }));
  } else {
    chapters = [{ title: "Whole book", start: 0, end: Infinity, w: [], t: [] }];
  }

  const baseName = audio.name.replace(/\.[^.]+$/, "").replace(/\.lyraea$/i, "").replace(/_audio_ebook$/i, "");
  const id = `book:${audio.name}:${audio.size}`;
  return {
    id,
    local: true,                    // never persisted to the catalogue, never play-counted
    fileName: audio.name,
    src: URL.createObjectURL(audio),
    title: book?.title || tag.title || baseName,
    artist: tag.artist || "Lyraei Reader",
    albumTitle: "Audiobook",
    durationSec: Number.isFinite(chapters[chapters.length - 1].end) ? chapters[chapters.length - 1].end : 0,
    book: { chapters, timed: chapters.some((c) => c.w.length), timing: book?.timing || [] },
  };
}

// ── Where am I ───────────────────────────────────────────────────────
export function chapterAt(book, sec) {
  const cs = book?.chapters || [];
  let lo = 0, hi = cs.length - 1, ans = 0;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    if (cs[m].start <= sec + 0.001) { ans = m; lo = m + 1; } else hi = m - 1;
  }
  return ans;
}

// Index of the word being spoken at `sec`, or -1 before the first word.
export function wordAt(chapter, sec) {
  const t = chapter?.t; if (!t?.length) return -1;
  const ms = (sec - chapter.start) * 1000;
  let lo = 0, hi = t.length / 2 - 1, ans = -1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    if (t[m * 2] <= ms) { ans = m; lo = m + 1; } else hi = m - 1;
  }
  return ans;
}

// Readers keeps markdown in chapter text, and the cues are its words —
// so "**Shatterfield**" and table pipes arrive here. Show clean words;
// a token that is ONLY markup renders as nothing but keeps its timing.
export const cleanWord = (w) => String(w || "").replace(/[*_#`>|]+/g, "").replace(/^-{2,}$/, "").replace(/^:?-{3,}:?$/, "");

// ── Resume ───────────────────────────────────────────────────────────
// Keyed by file identity, because the blob URL is new every time.
const KEY = (id) => `lyraea.book.v1.${id}`;
export function loadBookState(id) {
  try { return JSON.parse(localStorage.getItem(KEY(id)) || "null") || {}; } catch { return {}; }
}
export function saveBookState(id, patch) {
  try { localStorage.setItem(KEY(id), JSON.stringify({ ...loadBookState(id), ...patch, at: Date.now() })); } catch { /* private mode */ }
}
